import { h, Fragment } from 'preact'

import Form from 'components/Form'
import Switch from 'components/Switch'
import TextArea from 'components/TextArea'
import Alert from 'components/Alert'
import OrganizationEditForm from 'components/OrganizationEditForm'

export default function SupportSettingsPage({
  organization, changes, setChanges
}) {
  return <OrganizationEditForm {...{
    className: 'OrganizationAdminPage-padded',
    organization,
    value: changes,
    onChange: setChanges,
    render: props => {
      return <Fragment>
        <SupportEnableSection {...{...props, organization}}/>
        <SupportMessageSection {...{...props, organization}}/>
        <SupportInfoSection {...{...props, organization}}/>
        {props.formControls()}
      </Fragment>
    }
  }}/>
}

function SupportEnableSection({ form }) {
  return <Form.Row>
    <Form.Item>
      <Form.Label>ENABLE SUPPORT</Form.Label>
      <p>
        Allow members to support your hub with a one-time payment.
         Funds go directly to the hub creator's Stripe account.
      </p>
      {
        form.bindInput({
          valueProp: 'support_enabled',
          validateOnChange: true,
          input: <Switch />
        })
      }
    </Form.Item>
  </Form.Row>
}

function SupportMessageSection({ form, organization }) {
  const supportEnabled = form.getValue('support_enabled') ?? organization.support_enabled
  const supportMessage = form.getValue('support_message') ?? organization.support_message ?? ''
  const messageLength = supportMessage.length
  const isMessageTooLong = messageLength > 500

  return <Form.Row>
    <Form.Item>
      <Form.Label>SUPPORT MESSAGE {supportEnabled && <span style={{ color: '#e74c3c' }}>*</span>}</Form.Label>
      <p>
        This message will be shown to potential supporters when they click the support button.
        Maximum 500 characters.
      </p>
      {
        form.bindInput({
          valueProp: 'support_message',
          validateOnChange: true,
          input: <TextArea
            disabled={!supportEnabled}
            required={supportEnabled}
            maxLength={500}
            rows={4}
            placeholder="Thank you for supporting our hub! Your contribution helps us..."
            style={supportEnabled ? {} : { opacity: 0.5 }}
          />
        })
      }
      <div style={{
        fontSize: '0.875rem',
        marginTop: '0.5rem',
        color: isMessageTooLong ? '#e74c3c' : '#666'
      }}>
        {messageLength} / 500 characters
      </div>
      {!supportEnabled && (
        <div style={{
          fontSize: '0.875rem',
          marginTop: '0.5rem',
          color: '#666',
          fontStyle: 'italic'
        }}>
          Enable support to edit the message
        </div>
      )}
    </Form.Item>
  </Form.Row>
}

function SupportInfoSection({ form, organization }) {
  const supportEnabled = form.getValue('support_enabled') ?? organization.support_enabled
  const hasRecurringPrices = organization.support_stripe_recurring_prices &&
                             Object.keys(organization.support_stripe_recurring_prices).length > 0

  if (supportEnabled) {
    return <Fragment>
      <Form.Row>
        <Form.Item>
          <Alert type="info">
            <strong>Patreon Feature Active</strong>
            <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
              <li>A support button (outlined heart icon) appears next to Join/Leave hub button</li>
              <li>Supporters can choose from preset amounts: $1, $3, $5, $10, $20, $50, $100, $500</li>
              <li>Funds go 100% to the hub creator's connected Stripe account</li>
              <li>Supporters will see a private badge but won't be publicly listed</li>
              <li>You can view supporters in the "Supporters" tab</li>
            </ul>
          </Alert>
        </Form.Item>
      </Form.Row>
      {supportEnabled && !hasRecurringPrices && (
        <Form.Row>
          <Form.Item>
            <Alert type="warning">
              <strong>New Feature Available: Monthly Recurring Support</strong>
              <div style={{ marginTop: '0.5rem' }}>
                To enable monthly recurring support option for your supporters:
                <ol style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li>Disable support below</li>
                  <li>Re-enable support with your message</li>
                  <li>Both one-time and recurring options will be available</li>
                </ol>
              </div>
            </Alert>
          </Form.Item>
        </Form.Row>
      )}
    </Fragment>
  }

  return <Form.Row>
    <Form.Item>
      <Alert type="info">
        <strong>How Support Works:</strong>
        <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
          <li>Enter a support message to show potential supporters</li>
          <li>Toggle "Enable Support" to activate the feature</li>
          <li>Supporters choose from preset amounts ($1-$100) on Stripe checkout</li>
          <li>An outlined heart button appears next to Join/Leave hub button</li>
          <li>To change settings, you must first disable support</li>
        </ul>
      </Alert>
    </Form.Item>
  </Form.Row>
}
